import React, {useContext, useEffect, useRef, useState} from 'react';
import {Image, BackHandler, ScrollView, useWindowDimensions, Platform, ImageBackground} from 'react-native';
import Step1 from './steps/ContactInfo';
import Step2 from './steps/TermsConditions';
import MainStyle from '../../../../MainStyle';
import IdVerification from "./steps/IdVerification";
import kiosk_logo_RGB_1row from "../../../assets/img/evenplay-logo-long.svg";
import {StoreContext} from "../../../components/StoreContextProvider";
import {
  LANDING_PAGE_URL, breakpoint, isFullSwing, ActivePromoTypes, FS_WELCOME_ONBOARDING_PENDING_KEY
} from "../../../constants";
import bg from "../../../assets/img/Loading login page-001d.jpg";
import {Ionicons} from '@expo/vector-icons';
import {useNavigation} from "@react-navigation/native";
import HelpLinkModalTrigger from "../../../modals/HelpLinksModal";
import Div from "../../../lib/Div";
import P from "../../../lib/P";
import {colors} from "../../../assets/styles/colors";
import FSHeader from "../../../components/FSHeader";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import AsyncStorage from "@react-native-async-storage/async-storage";

type Props = { route: any };

const SignUpScreen = ({route}: Props) => {
  const [step, setStep] = useState(1);
  const backhandlerRef = useRef<any>();
  const Context = useContext(StoreContext);
  const {cancelModalConfig} = Context;
  const {width} = useWindowDimensions();
  const isLg = width >= breakpoint;
  const navigation: NativeStackNavigationProp<any> = useNavigation();

  const goToCSR = () => {
    if (Platform.OS === 'web') window.open('https://smgames.atlassian.net/servicedesk/customer/portal/3', isLg ? '_self' : '_blank');
  };

  const modalClose = () => {
    Context.setCancelModalConfig({...cancelModalConfig, visible: false});
  };

  const onCancel = () => {
    Context.setCancelModalConfig({
      visible: true, title: 'CANCEL SIGN UP', subtitle: 'Are you sure you want to exit?',
      secondary: `All information entered earlier will be ${step === 1 ? 'deleted' : 'saved'}`,
      onExit, onResume: modalClose
    });

    return true;
  };

  const onExit = async () => {
    modalClose();

    if (isFullSwing) {
      // SMG-3017: a FullSwing user who exits before accepting T&C is NOT
      // disabled server-side. PartnerAuth re-gates any pre-T&C user back to the
      // Register/T&C flow on next login (PartnerAuth: !user.tncAccepted ->
      // navigate('Register')), so the prior
      // partner-service.FullSwingCancelRegister disable call was redundant.
      // Dropping the caller also unblocks the partner-service decommission
      // (SMG-3009): the RPC is now caller-less.
      try {
        await Context.logout();

      } catch (error) {
        console.warn('Logout failed after FullSwing registration exit', error);
      }

      setStep(1);

      if (backhandlerRef?.current?.remove) backhandlerRef.current.remove();

      const referrer = await AsyncStorage.getItem('referrerOrigin');

      if (Platform.OS === 'web') window.open(referrer || LANDING_PAGE_URL, '_self');

      return;

    } else {
      setStep(1);

      if (backhandlerRef?.current?.remove) backhandlerRef.current.remove();

      await Context.logout();

      if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
    }
  };

  const GetUser = async () => {
    const promoType = (await AsyncStorage.getItem('promoType')) || '';

    // FSG-8881: registration (profile + T&C) is complete — perform the
    // session join the server deferred at the initial FullSwingAuth
    // (implicit joins are skipped while tnc_accepted=false, so the new
    // user never appeared on the in-game roster mid-signup). The re-run
    // also refreshes the smg session, so the user's chosen screen name —
    // not the provisioned "First Last" placeholder — is what the
    // simulator renders on join. Best-effort like the pre-BSA
    // FullSwingCheckIn: a failure here must not strand the user on the
    // registration screen; they can re-scan the QR to join.
    if (isFullSwing) {
      try {
        await Context.completeRegistrationJoin();
      } catch {
        // StoreContextProvider already reports the normalized failure with its
        // auth category/code. Never log the original error here: the provider
        // object may contain transport metadata or credential-related details.
        console.warn('[FullSwingAuth] post-registration session join did not complete');
      }
    }

    Context.getUser().then(async () => {
      if (backhandlerRef?.current?.remove) backhandlerRef.current.remove();
      await AsyncStorage.setItem(FS_WELCOME_ONBOARDING_PENDING_KEY, 'true').catch(err =>
        console.warn('welcome onboarding pending flag setItem err', err),
      );

      if (ActivePromoTypes.includes(promoType)) navigation.navigate('tournaments');
      // SMG-3047: in-app redirect instead of window.open('_self'). The full-page
      // reload re-invoked the cache-first PWA service worker, which served a
      // stale shell on the fresh navigation and rendered a black screen (no
      // network request — the cached bundle failed to mount). React Navigation
      // keeps the session/Context alive and passes fromRegistrationPage as a
      // route param (BetSlip reads route.params.fromRegistrationPage directly).
      else navigation.reset({index: 0, routes: [{name: 'challenges', params: {fromRegistrationPage: true}}]});
    });
  };

  useEffect(() => {
    if (route.params?.step) setStep(+route.params?.step);

    if (Platform.OS === 'web') return;

    const backHandler = BackHandler.addEventListener('hardwareBackPress', onCancel);
    backhandlerRef.current = backHandler;

    return () => {
      backHandler.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params?.step]);

  return (
    <Div bg={isFullSwing ? '#131314' : colors.loading_login} flex={1}>
      <ImageBackground source={isFullSwing ? undefined : bg} style={MainStyle.flex1} imageStyle={{opacity: 0.8}}>
        {!isFullSwing && <HelpLinkModalTrigger position="absolute" top={5} right={5}/>}

        <ScrollView contentContainerStyle={MainStyle.min_h_full} keyboardShouldPersistTaps="handled">
          <Div w="100%" maxW={430} mx="auto" h="100%">
            {isFullSwing ? (
              <FSHeader
                title={step === 1 ? 'Complete Your Profile' : step === 2 ? 'New Player Info' : 'Verify Your Identity'}
                goBack={onCancel} px={20} hideLogo={step === 3} hideAccount hideMenu hideGameLogo hideBalance
              />
            ) : (
              <Div mt={7} row justifyContent="space-between" alignItems="center" mb={20} mx={20}>
                <Ionicons name="chevron-back" size={24} color="white" onPress={onCancel}/>
                <Image source={kiosk_logo_RGB_1row as any} style={[MainStyle.h_46, MainStyle.w_176]}/>
                <Div w={24} h={24}/>
              </Div>
            )}

            {!isFullSwing && (
              <>
                <Div row justifyContent="space-between" mb={12} alignItems="center" mx={20}>
                  <P>{step === 1 ? 'Contact Information' : step === 2 ? 'Terms & Conditions' : 'Photo'}</P>
                  <P fw="500" fs={24}>{step}/<P fw="500" fs={24} color={colors.darkText}>3</P></P>
                </Div>

                <Div h={4} bg={colors.popupDropBg} mb={60} rounded={200} mx={20}>
                  <Div bg={colors.filter_chip_bg} rounded={200} h="100%" w={`${step * 33.3}%`}/>
                </Div>
              </>
            )}

            {step === 1 && (
              <Step1 goNext={() => navigation.navigate({name: 'Register', params: {step: 2}})} onCancel={onCancel}/>
            )}

            {/*{step === 1 && (*/}
            {/*  <IdVerification*/}
            {/*    goNextStep={GetUser} goCsr={goToCSR} onCancel={onCancel} title="Selfie"*/}
            {/*    subtitle="Please take a clear photo of yourself"*/}
            {/*  />*/}
            {/*)}*/}

            {step === 2 && (
              <Step2
                goNext={() => {
                  if (isFullSwing) GetUser();
                  else navigation.navigate({name: 'Register', params: {step: 3}});
                }}
                goBack={onCancel}
              />
            )}

            {step === 3 && (
              <IdVerification
                goNextStep={GetUser} goCsr={goToCSR} onCancel={onCancel}
                title={isFullSwing ? 'Take a Selfie' : 'Selfie'}
                subtitle="Please take a clear photo of yourself"
              />
            )}
          </Div>
        </ScrollView>
      </ImageBackground>
    </Div>
  );
};

export default SignUpScreen;
